import { useEffect, useState } from 'react';
import { cva } from 'class-variance-authority';
import { Flex, Text, Button, message, Icon } from '@nula/core-ui';
import { apiFetch } from '@/lib/http/client';
import type { PagedResponse } from '@/lib/http/types';
import { useNavigateToEntity } from '@/hooks/navigation';
import styles from './pending-invitations-banner.module.css';

const root = cva(styles.root);
const bannerItem = cva(styles.bannerItem);

interface PendingInvitationItem {
  id: string;
  token: string;
  email: string;
  permission: string;
  kind: 'document' | 'collection' | 'recording';
  resourceId: string;
  resourceTitle: string;
  invitedByName: string | null;
}

interface AcceptResponse {
  kind: 'document' | 'collection' | 'recording';
  resourceId: string;
  permission: string;
}

export function PendingInvitationsBanner() {
  const navigateToEntity = useNavigateToEntity();
  const [invitations, setInvitations] = useState<PendingInvitationItem[]>([]);
  const [accepting, setAccepting] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<PagedResponse<PendingInvitationItem>>('/me/pending-invitations')
      .then((data) => setInvitations(data.items ?? []))
      .catch(() => {
        // Silently fail — this is a non-critical enhancement
      });
  }, []);

  const handleAccept = async (inv: PendingInvitationItem) => {
    setAccepting(inv.id);
    try {
      const data = await apiFetch<AcceptResponse>(`/invitations/${inv.token}/accept`, { method: 'POST' });
      message.success(`You now have access to "${inv.resourceTitle}".`);
      setInvitations((prev) => prev.filter((i) => i.id !== inv.id));
      navigateToEntity(data.resourceId);
    } catch {
      message.error('Failed to accept invitation');
    } finally {
      setAccepting(null);
    }
  };

  const handleDismiss = (id: string) => {
    setInvitations((prev) => prev.filter((i) => i.id !== id));
  };

  if (invitations.length === 0) return null;

  return (
    <Flex vertical gap="sm" className={root()}>
      {invitations.map((inv) => (
        <Flex key={inv.id} align="center" gap="sm" className={bannerItem()}>
          <Icon name="mail" size={16} />
          <Flex vertical className={styles.info}>
            <Text size="sm">
              <Text size="sm" strong>{inv.invitedByName ?? 'Someone'}</Text>
              {' invited you to '}
              <Text size="sm" strong>{inv.resourceTitle}</Text>
            </Text>
          </Flex>
          <Flex gap="xs" align="center" className={styles.actions}>
            <Button
              variant="primary"
              size="sm"
              loading={accepting === inv.id}
              disabled={accepting !== null}
              onClick={() => void handleAccept(inv)}
              aria-label={`Accept invitation to ${inv.resourceTitle}`}
            >
              Accept
            </Button>
            <Button
              variant="text"
              size="sm"
              onClick={() => handleDismiss(inv.id)}
              aria-label={`Dismiss invitation to ${inv.resourceTitle}`}
            >
              Dismiss
            </Button>
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
}
